import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MeasurementUnit {
  id: string;
  name: string;
  abbreviation: string;
  status: string;
  created_at: string;
}

export const useGetMeasurementUnits = () => {
  return useQuery({
    queryKey: ['measurement-units'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('measurement_units')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as MeasurementUnit[];
    },
  });
};

export const useGetActiveMeasurementUnits = () => {
  return useQuery({
    queryKey: ['measurement-units', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('measurement_units')
        .select('*')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data as MeasurementUnit[];
    },
  });
};

export const useCreateMeasurementUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unit: { name: string; abbreviation: string }) => {
      const { data, error } = await supabase
        .from('measurement_units')
        .insert(unit)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['measurement-units'] }),
  });
};

export const useUpdateMeasurementUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unit: { id: string; name: string; abbreviation: string; status: string }) => {
      const { data, error } = await supabase
        .from('measurement_units')
        .update({ name: unit.name, abbreviation: unit.abbreviation, status: unit.status })
        .eq('id', unit.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['measurement-units'] }),
  });
};

export const useDeleteMeasurementUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('measurement_units')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['measurement-units'] }),
  });
};
